import * as Y from 'yjs';
import { nanoid } from 'nanoid';
import { withLocalTransaction } from '../transact';
import { yElements, yLines } from '../schema';
import { trackCanvasEvent } from '../../analytics';

const _prevTypeByColumnId = new Map();

const COLUMN_FIELDS = [
    'id', 'name', 'type', 'description',
    'isPK', 'isFK', 'fkRefTable', 'fkRefColumn',
    'isNotNull', 'isAutoIncrement',
    'isEnum', 'enumValues',
    'defaultValue', 'generatedValue', 'checkValue',
    'indexType',
];

// Tables grow / shrink in 36-pixel steps as columns are added or removed.
// Matches the legacy reducer (TABLE_ROW_HEIGHT in old_index.js).
const TABLE_ROW_HEIGHT = 36;

function adjustTableHeight(yTable, delta) {
    const current = yTable.get('height') ?? 0;
    yTable.set('height', current + delta);
}

function makeYColumn(column) {
    const yCol = new Y.Map();
    for (const k of COLUMN_FIELDS) {
        if (column[k] !== undefined) yCol.set(k, column[k]);
    }
    return yCol;
}

function getTable(doc, tableId) {
    return yElements(doc).get(tableId);
}

function getColumn(doc, tableId, columnId) {
    return getTable(doc, tableId)?.get('columns')?.get(columnId);
}

function indexOfInOrder(yArray, value) {
    return yArray.toArray().indexOf(value);
}

function moveInOrder(yArray, fromIdx, toIdx, value) {
    yArray.delete(fromIdx, 1);
    yArray.insert(toIdx, [value]);
}

// ---------------------------------------------------------------------------
// setTableDisplayHeight
// ---------------------------------------------------------------------------
// User-shrunk display height for column clipping. `height` stays
// content-derived (adjustTableHeight); this field exists only while the
// user has the table clipped — null/undefined clears it (expand).
export function setTableDisplayHeight(doc, tableId, displayHeight) {
    withLocalTransaction(doc, () => {
        const yTable = getTable(doc, tableId);
        if (!yTable) return;
        if (displayHeight == null) {
            yTable.delete('displayHeight');
        } else {
            yTable.set('displayHeight', displayHeight);
        }
    });
}

// ---------------------------------------------------------------------------
// addColumn
// ---------------------------------------------------------------------------
export function addColumn(doc, { tableId, mode, refColumnId, column }) {
    let ok = false;
    withLocalTransaction(doc, () => {
        const yTable = getTable(doc, tableId);
        if (!yTable) return;
        const cols  = yTable.get('columns');
        const order = yTable.get('columnsOrder');
        cols.set(column.id, makeYColumn(column));

        const orderArr = order.toArray();
        let insertAt;
        if      (mode === 'above')                     insertAt = Math.max(0, orderArr.indexOf(refColumnId));
        else if (mode === 'below')                     insertAt = orderArr.indexOf(refColumnId) + 1;
        else if (mode === 'first' || mode === 'start') insertAt = 0;
        else                                           insertAt = orderArr.length;

        order.insert(insertAt, [column.id]);
        adjustTableHeight(yTable, TABLE_ROW_HEIGHT);
        ok = true;
    });
    if (ok) trackCanvasEvent('app_canvas_column_add');
}

// ---------------------------------------------------------------------------
// deleteColumn
// ---------------------------------------------------------------------------
export function deleteColumn(doc, tableId, columnId) {
    let ok = false;
    withLocalTransaction(doc, () => {
        const yTable = getTable(doc, tableId);
        if (!yTable) return;

        // cascade lines
        const lines = yLines(doc);
        for (const lineId of [...lines.keys()]) {
            const line = lines.get(lineId);
            // Endpoints use source/target* names (from/to* kept as a legacy
            // fallback). Without this the connected line was left orphaned when its
            // column was deleted.
            const fromEl  = line.get('sourceElementId') ?? line.get('fromElementId');
            const fromCol = line.get('sourceColumnId')  ?? line.get('fromColumnId');
            const toEl    = line.get('targetElementId') ?? line.get('toElementId');
            const toCol   = line.get('targetColumnId')  ?? line.get('toColumnId');
            const matches =
                (fromEl === tableId && fromCol === columnId) ||
                (toEl   === tableId && toCol   === columnId);
            if (matches) lines.delete(lineId);
        }

        // remove from columnsOrder (only adjust height if it was actually present)
        const order = yTable.get('columnsOrder');
        const idx = indexOfInOrder(order, columnId);
        if (idx < 0) return;
        order.delete(idx, 1);

        // remove from columns map
        yTable.get('columns').delete(columnId);
        adjustTableHeight(yTable, -TABLE_ROW_HEIGHT);
        ok = true;
    });
    if (ok) trackCanvasEvent('app_canvas_column_delete');
}

// ---------------------------------------------------------------------------
// moveColumnAbove / moveColumnBelow
// ---------------------------------------------------------------------------
export function moveColumnAbove(doc, tableId, columnId) {
    withLocalTransaction(doc, () => {
        const yTable = getTable(doc, tableId);
        if (!yTable) return;
        const order = yTable.get('columnsOrder');
        const idx = indexOfInOrder(order, columnId);
        if (idx <= 0) return;
        moveInOrder(order, idx, idx - 1, columnId);
    });
}

export function moveColumnBelow(doc, tableId, columnId) {
    withLocalTransaction(doc, () => {
        const yTable = getTable(doc, tableId);
        if (!yTable) return;
        const order = yTable.get('columnsOrder');
        const arr = order.toArray();
        const idx = arr.indexOf(columnId);
        if (idx < 0 || idx >= arr.length - 1) return;
        moveInOrder(order, idx, idx + 1, columnId);
    });
}

// ---------------------------------------------------------------------------
// duplicateColumn
// ---------------------------------------------------------------------------
export function duplicateColumn(doc, tableId, columnId) {
    let newId = null;
    let ok = false;
    withLocalTransaction(doc, () => {
        const yTable = getTable(doc, tableId);
        if (!yTable) return;
        const cols  = yTable.get('columns');
        const order = yTable.get('columnsOrder');
        const src = cols.get(columnId);
        if (!src) return;

        newId = nanoid();
        const dup = new Y.Map();
        src.forEach((value, key) => dup.set(key, value));
        dup.set('id', newId);
        cols.set(newId, dup);

        const idx = indexOfInOrder(order, columnId);
        order.insert(idx + 1, [newId]);
        adjustTableHeight(yTable, TABLE_ROW_HEIGHT);
        ok = true;
    });
    if (ok) trackCanvasEvent('app_canvas_column_duplicate');
    return newId;
}

// ---------------------------------------------------------------------------
// Simple field setters
// ---------------------------------------------------------------------------
export function setColumnName(doc, tableId, columnId, name) {
    withLocalTransaction(doc, () => {
        const col = getColumn(doc, tableId, columnId);
        if (!col) return;
        col.set('name', name);
    });
}

export function setColumnType(doc, tableId, columnId, type) {
    withLocalTransaction(doc, () => {
        const col = getColumn(doc, tableId, columnId);
        if (!col) return;
        col.set('type', type);
    });
}

export function setColumnDescription(doc, tableId, columnId, description) {
    let ok = false;
    withLocalTransaction(doc, () => {
        const col = getColumn(doc, tableId, columnId);
        if (!col) return;
        col.set('description', description);
        ok = true;
    });
    if (ok) trackCanvasEvent('app_canvas_column_description_add');
}

// ---------------------------------------------------------------------------
// setColumnKey
// ---------------------------------------------------------------------------
export function setColumnKey(doc, tableId, columnId, keyType) {
    let ok = false;
    withLocalTransaction(doc, () => {
        const col = getColumn(doc, tableId, columnId);
        if (!col) return;
        if (!keyType) {
            col.set('isPK', false);
            col.set('isFK', false);
            col.set('fkRefTable', null);
            col.set('fkRefColumn', null);
        } else if (keyType === 'pk') {
            col.set('isPK', true);
            col.set('isFK', false);
            col.set('fkRefTable', null);
            col.set('fkRefColumn', null);
        } else {
            // fk
            col.set('isPK', false);
            col.set('isFK', true);
            col.set('fkRefTable', null);
            col.set('fkRefColumn', null);
        }
        ok = true;
    });
    if (ok) trackCanvasEvent('app_canvas_column_key_set', { key_type: keyType });
}

// ---------------------------------------------------------------------------
// setColumnIndex
// ---------------------------------------------------------------------------
export function setColumnIndex(doc, tableId, columnId, indexType) {
    withLocalTransaction(doc, () => {
        const col = getColumn(doc, tableId, columnId);
        if (!col) return;
        col.set('indexType', indexType ?? null);
    });
}

// ---------------------------------------------------------------------------
// Toggles
// ---------------------------------------------------------------------------
export function setColumnNotNull(doc, tableId, columnId) {
    withLocalTransaction(doc, () => {
        const col = getColumn(doc, tableId, columnId);
        if (!col) return;
        col.set('isNotNull', !col.get('isNotNull'));
    });
}

export function setColumnAutoIncrement(doc, tableId, columnId) {
    withLocalTransaction(doc, () => {
        const col = getColumn(doc, tableId, columnId);
        if (!col) return;
        col.set('isAutoIncrement', !col.get('isAutoIncrement'));
    });
}

// ---------------------------------------------------------------------------
// setColumnEnum
// ---------------------------------------------------------------------------
export function setColumnEnum(doc, tableId, columnId, values) {
    withLocalTransaction(doc, () => {
        const col = getColumn(doc, tableId, columnId);
        if (!col) return;
        const isEnum = !!values && (Array.isArray(values) ? values.length > 0 : true);

        if (isEnum) {
            const currentType = col.get('type');
            if (currentType !== 'enum') _prevTypeByColumnId.set(columnId, currentType);
            col.set('isEnum', true);
            col.set('type', 'enum');
            col.set('enumValues', values);
        } else if (col.get('type') === 'enum') {
            const prevType = _prevTypeByColumnId.get(columnId) ?? 'int';
            _prevTypeByColumnId.delete(columnId);
            col.set('type', prevType);
            col.delete('isEnum');
            col.delete('enumValues');
        }
        // falsy + type !== 'enum': no-op
    });
}

// ---------------------------------------------------------------------------
// Optional value setters (truthy sets, falsy deletes)
// ---------------------------------------------------------------------------
export function setColumnDefaultValue(doc, tableId, columnId, value) {
    withLocalTransaction(doc, () => {
        const col = getColumn(doc, tableId, columnId);
        if (!col) return;
        if (value) col.set('defaultValue', value);
        else       col.delete('defaultValue');
    });
}

export function setColumnGeneratedValue(doc, tableId, columnId, value) {
    withLocalTransaction(doc, () => {
        const col = getColumn(doc, tableId, columnId);
        if (!col) return;
        if (value) col.set('generatedValue', value);
        else       col.delete('generatedValue');
    });
}

export function setColumnCheckValue(doc, tableId, columnId, value) {
    withLocalTransaction(doc, () => {
        const col = getColumn(doc, tableId, columnId);
        if (!col) return;
        if (value) col.set('checkValue', value);
        else       col.delete('checkValue');
    });
}
